import { AxiosError, AxiosResponse } from 'axios';
import apiClient from './apiClient';
import.meta.env;
import { PublicCourse, CoursesResponse, CourseDetailResponse, SearchFilters, SortOption, PublicClassroom } from '@/types/course';

// Retry configuration
const RETRY_ATTEMPTS = 3;
const RETRY_DELAY = 1000; // 1 second base delay

// Using centralized API client with automatic token refresh
const api = apiClient;

// Utility function for exponential backoff retry
const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

async function retryRequest<T>(
  requestFn: () => Promise<AxiosResponse<T>>,
  attempts: number = RETRY_ATTEMPTS
): Promise<AxiosResponse<T>> {
  for (let i = 0; i < attempts; i++) {
    try {
      return await requestFn();
    } catch (error) {
      const isLastAttempt = i === attempts - 1;
      const isRetryableError = error instanceof AxiosError && 
        (error.code === 'NETWORK_ERROR' || 
         error.code === 'ECONNABORTED' ||
         (error.response?.status && error.response.status >= 500));

      if (isLastAttempt || !isRetryableError) {
        throw error;
      }

      // Exponential backoff: wait longer between each retry
      const delayMs = RETRY_DELAY * Math.pow(2, i);
      console.log(`Request failed (attempt ${i + 1}/${attempts}), retrying in ${delayMs}ms...`);
      await sleep(delayMs);
    }
  }
  
  throw new Error('Max retry attempts exceeded');
}

// Add request interceptor for logging
api.interceptors.request.use(
  (config) => {
    console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Enhanced user-friendly error messages
function getUserFriendlyErrorMessage(error: AxiosError): string {
  if (!error.response) {
    if (error.code === 'NETWORK_ERROR') {
      return 'Unable to connect to the server. Please check your internet connection and try again.';
    }
    if (error.code === 'ECONNABORTED') {
      return 'Request timed out. The server is taking too long to respond. Please try again.';
    }
    return 'Network error. Please check your connection and try again.';
  }
  
  const status = error.response.status;
  const data = error.response.data as any;
  
  switch (status) {
    case 400:
      return data?.message || 'Invalid request. Please check your input and try again.';
    case 401:
      return 'Authentication required. Please sign in to continue.';
    case 403:
      return 'Access denied. You don\'t have permission to access this resource.';
    case 404:
      return data?.message || 'The requested resource was not found.';
    case 429:
      return 'Too many requests. Please wait a moment and try again.';
    case 500:
      return 'Server error. Our team has been notified. Please try again later.';
    case 502:
      return 'Service temporarily unavailable. Please try again in a few minutes.';
    case 503:
      return 'Service maintenance in progress. Please try again later.';
    default:
      return data?.message || `An unexpected error occurred (${status}). Please try again.`;
  }
}

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error: AxiosError) => {
    console.error('API Response Error:', error);
    
    const userMessage = getUserFriendlyErrorMessage(error);
    throw new Error(userMessage);
  }
);

export interface PaginatedCoursesResponse {
  courses: PublicCourse[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalCourses: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

export class CourseApi {
  /**
   * Fetch courses with optional search and filters
   */
  static async getCourses(filters: SearchFilters = {}): Promise<PaginatedCoursesResponse> {
    try {
      const params = new URLSearchParams();
      
      // Add search parameters
      if (filters.search) {
        params.append('search', filters.search);
      }
      
      if (filters.method) {
        const methods = Array.isArray(filters.method) ? filters.method : [filters.method];
        methods.forEach(method => params.append('method', method));
      }
      
      if (filters.duration) {
        const durations = Array.isArray(filters.duration) ? filters.duration : [filters.duration];
        durations.forEach(duration => params.append('duration', duration));
      }
      
      if (filters.sort) {
        params.append('sort', filters.sort);
      }
      
      if (filters.sortDirection) {
        params.append('sortDirection', filters.sortDirection);
      }
      
      if (filters.page) {
        params.append('page', filters.page.toString());
      }
      
      if (filters.limit) {
        params.append('size', filters.limit.toString());
      }

      if (filters.audience) {
        params.append('audience', filters.audience);
      }
      
      const response = await retryRequest(
        () => api.get<CoursesResponse>(`/public/courses?${params.toString()}`)
      );
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch courses');
      }
      
      const courses = response.data.data;
      const pagination = response.data.pagination;
      
      return {
        courses,
        pagination: {
          currentPage: pagination?.current || 1,
          totalPages: pagination?.totalPages || 1,
          totalCourses: pagination?.total || courses.length,
          hasNextPage: (pagination?.current || 1) < (pagination?.totalPages || 1),
          hasPrevPage: (pagination?.current || 1) > 1,
        }
      };
    } catch (error) {
      console.error('Error fetching courses:', error);
      throw error;
    }
  }
  
  /**
   * Fetch a single course by ID
   */
  static async getCourse(id: string): Promise<PublicCourse> {
    try {
      const response = await retryRequest(
        () => api.get<CourseDetailResponse>(`/public/courses/${id}`)
      );
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch course');
      }
      
      return response.data.data;
    } catch (error) {
      console.error(`Error fetching course ${id}:`, error);
      throw error;
    }
  }

  static async getGiftOffers(courseIdOrHash: string | number): Promise<{
    asSource: Array<{
      giftCourseId: number;
      giftCourseName: string;
      giftCourseCode: string;
      giftCourseHashId: string | null;
      giftCourseSlug: string | null;
      giftCourseFee?: number;
      sourceCourseFee?: number;
    }>;
    asGift: Array<{
      sourceCourseId: number;
      sourceCourseName: string;
      sourceCourseCode: string;
      sourceCourseHashId: string | null;
      sourceCourseSlug: string | null;
      sourceCourseFee?: number;
      giftCourseFee?: number;
    }>;
  }> {
    try {
      const response = await api.get<{
        success: boolean;
        data: {
          asSource: Array<{
            giftCourseId: number;
            giftCourseName: string;
            giftCourseCode: string;
            giftCourseHashId: string | null;
            giftCourseSlug: string | null;
            giftCourseFee?: number;
            sourceCourseFee?: number;
          }>;
          asGift: Array<{
            sourceCourseId: number;
            sourceCourseName: string;
            sourceCourseCode: string;
            sourceCourseHashId: string | null;
            sourceCourseSlug: string | null;
            sourceCourseFee?: number;
            giftCourseFee?: number;
          }>;
        };
      }>(`/public/courses/${courseIdOrHash}/gift-offers`);
      if (!response.data.success || !response.data.data) {
        return { asSource: [], asGift: [] };
      }
      return response.data.data;
    } catch {
      return { asSource: [], asGift: [] };
    }
  }

  /** Combo A tặng B cho homepage / catalog */
  static async getGiftCombos(): Promise<
    Array<{
      sourceCourseId: number;
      sourceCourseName: string;
      sourceCourseCode: string;
      sourceCourseHashId: string | null;
      sourceCourseFee: number;
      giftCourseId: number;
      giftCourseName: string;
      giftCourseCode: string;
      giftCourseHashId: string | null;
      giftCourseFee: number;
      originalFee: number;
      comboFee: number;
    }>
  > {
    try {
      const response = await api.get<{
        success: boolean;
        data: {
          items: Array<{
            sourceCourseId: number;
            sourceCourseName: string;
            sourceCourseCode: string;
            sourceCourseHashId: string | null;
            sourceCourseFee: number;
            giftCourseId: number;
            giftCourseName: string;
            giftCourseCode: string;
            giftCourseHashId: string | null;
            giftCourseFee: number;
            originalFee: number;
            comboFee: number;
          }>;
        };
      }>('/public/courses/gift-combos');
      if (!response.data.success || !response.data.data?.items) return [];
      return response.data.data.items;
    } catch {
      return [];
    }
  }
  
  /**
   * Search courses with highlighted results
   */
  static async searchCourses(searchQuery: string, filters: Omit<SearchFilters, 'search'> = {}): Promise<PaginatedCoursesResponse> {
    return this.getCourses({ ...filters, search: searchQuery });
  }
  
  /**
   * Get featured/popular courses for homepage
   */
  static async getFeaturedCourses(limit: number = 8): Promise<PublicCourse[]> {
    const result = await this.getCourses({ 
      sort: SortOption.NEWEST,
      sortDirection: 'desc',
      limit 
    });
    return result.courses;
  }
  
  /**
   * Get upcoming classrooms for a course
   */
  static async getCourseClassrooms(courseId: string): Promise<PublicClassroom[]> {
    try {
      const course = await this.getCourse(courseId);
      // Classrooms are returned with the course details in upcomingClasses
      return course.upcomingClasses || [];
    } catch (error) {
      console.error(`Error fetching classrooms for course ${courseId}:`, error);
      throw error;
    }
  }
  
  /**
   * Get active audiences for persona gateway
   */
  static async getAudiences(): Promise<import('@/types/course').Audience[]> {
    const response = await retryRequest(
      () => api.get<{ success: boolean; data: import('@/types/course').Audience[] }>('/public/audiences')
    );
    if (!response.data.success) {
      throw new Error('Failed to fetch audiences');
    }
    return response.data.data || [];
  }

  /**
   * Validate API health
   */
  static async healthCheck(): Promise<boolean> {
    try {
      const response = await retryRequest(
        () => api.get('/health')
      );
      return response.status === 200;
    } catch (error) {
      console.error('API health check failed:', error);
      return false;
    }
  }
}

export default CourseApi;